/**
 * BoardPackView — read-only, mobile-friendly live board pack view
 * accessed via the cover QR on the exported PDF.
 *
 * URL shape: `/board-pack/:date?tenant=:tenant_id`
 *
 * The PDF is a snapshot at the moment of export; this SPA page is the
 * **live** equivalent — so when a director scans the QR on the train,
 * they see the same KPIs with the latest data and can tap a risk to
 * dig in.
 *
 * Permission model: behind the same `dashboard.read` permission as the
 * export endpoint. If the visitor isn't signed in, the regular login
 * wall kicks in (via the global token check in `lib/api.js`).
 */
import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { ArrowRight } from "lucide-react";

export default function BoardPackView() {
  const { date } = useParams();
  const [params] = useSearchParams();
  const tenantFromQuery = params.get("tenant");

  const [summary, setSummary] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const { data } = await api.get("/dashboard/summary");
        if (live) setSummary(data);
      } catch (e) {
        if (live) setError(e?.response?.data?.detail || "Couldn't load the live view.");
      }
    })();
    return () => {
      live = false;
    };
  }, [tenantFromQuery]);

  const kpis = summary?.kpis || {};
  const heat = summary?.heatmap || [];

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6 sm:py-10" data-testid="board-pack-view-page">
      <Seo title={`Board pack · ${date}`} path={`/board-pack/${date}`} />

      <header className="border-b border-violet-200 pb-4">
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-violet-700">Live board pack</p>
        <h1 className="mt-1 text-2xl font-semibold text-slate-900 sm:text-3xl">As of {date}</h1>
        <p className="mt-1 text-xs text-slate-500">
          You&apos;re looking at the live state. The PDF you received is the snapshot at export.
        </p>
      </header>

      {error && (
        <div className="mt-6 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900" data-testid="board-pack-view-error">
          {error}
        </div>
      )}

      {summary && (
        <>
          <section className="mt-6" data-testid="board-pack-view-kpis">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-700">Headline</h2>
            <dl className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
              <KpiTile testid="kpi-open" label="Open risks" value={kpis.open_risks ?? "—"} />
              <KpiTile testid="kpi-critical" label="At critical residual" value={kpis.critical_risks ?? "—"} />
              <KpiTile testid="kpi-above-appetite" label="Above appetite" value={kpis.above_appetite ?? "—"} />
            </dl>
          </section>

          {heat.length > 0 && (
            <section className="mt-8" data-testid="board-pack-view-heatmap">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-700">Severity heatmap</h2>
              <ul className="mt-3 space-y-2">
                {heat.map((h) => (
                  <li
                    key={h.theme_id}
                    className="flex items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                  >
                    <span className="font-medium text-slate-900">{h.theme_name || h.theme_id}</span>
                    <span className="text-xs text-slate-600">
                      {h.high || 0} high · {h.medium || 0} med · {h.low || 0} low
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="mt-8 flex flex-wrap gap-3">
            <Link
              to={`/dashboard?as_of=${date}`}
              className="inline-flex items-center gap-2 rounded-lg bg-violet-700 px-5 py-2.5 text-sm font-medium text-white hover:bg-violet-800"
              data-testid="board-pack-view-open-dashboard"
            >
              Open the full dashboard <ArrowRight size={16} />
            </Link>
            <Link
              to="/risks"
              className="inline-flex items-center gap-2 rounded-lg border border-violet-300 bg-white px-5 py-2.5 text-sm font-medium text-violet-800 hover:bg-violet-50"
              data-testid="board-pack-view-open-risks"
            >
              See the live risk register
            </Link>
          </section>
        </>
      )}
    </div>
  );
}

function KpiTile({ label, value, testid }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid={testid}>
      <div className="text-2xl font-semibold text-violet-700">{value}</div>
      <div className="mt-1 text-[10px] font-bold uppercase tracking-wide text-slate-500">{label}</div>
    </div>
  );
}
